import { Box, Text } from '@mantine/core'
import {
  ProgramBox,
  ProgramContent,
  type ProgramItem as ProgramItemProps,
  useProgram,
} from '@nessprim/planby-pro'
import clsx from 'clsx'

import { useQueryGetAllBookingProducts } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBookingProduct } from '@flecto/types'
import { usePopoverTrackedState } from '@flecto/ui'
import { useBookingStatus } from '@flecto/utils'

export const ProgramItem = ({
  isVerticalMode,
  program,
  ...rest
}: ProgramItemProps) => {
  const { t } = useTranslation('saas', { keyPrefix: 'schedule' })
  const { popoverIdOpened } = usePopoverTrackedState()

  const { getMouseEvents, isLive, styles } = useProgram({
    isVerticalMode,
    program,
    ...rest,
  })

  const {
    data: { booking, id, item },
  } = program

  const { data: bookingProducts } = useQueryGetAllBookingProducts({
    bookingId: booking.internal_id,
  })

  const { bookingStatus } = useBookingStatus({ booking })

  const programContentClasses = clsx('planby-program-content', {
    'planby-program-content-hover': popoverIdOpened === id,
  })

  const bgColor = `${bookingStatus.color}.0`
  const fontColor = `${bookingStatus.color}.9`

  const bookingItemQuantity =
    bookingProducts?.find(
      (bi: CoreBookingProduct) => bi.product?.internal_id === item?.internal_id
    )?.quantity ?? 0

  return (
    <ProgramBox
      style={styles.position}
      width={styles.width}
      {...getMouseEvents()}
      className='planby-program'
    >
      <ProgramContent
        className={programContentClasses}
        isLive={isLive}
        isVerticalMode={isVerticalMode}
        width={styles.width}
      >
        <Box bg={bgColor} p='sm' w='100%'>
          <Box mt={-1}>
            <Text c={fontColor} fw={500} size='sm' truncate>
              {booking.internal_id} {booking.customer?.full_name}
            </Text>
          </Box>
          <Box>
            <Text c={fontColor} size='xs' truncate>
              {t('item_quantity')}: {bookingItemQuantity}
            </Text>
          </Box>
        </Box>
      </ProgramContent>
    </ProgramBox>
  )
}
